'use es6';

import { Alignments } from 'rich-text-lib/constants/alignments';
import { TableDimensionTypes } from 'rich-text-lib/constants/table';
import { getPluginSetting } from '../utils';
import { TABLE_SETTINGS, hstableConfig, TableAlignmentClasses, TableAlignmentStyles } from '../constants/hstable2';
import { isResponsiveTableNode, isPercentage, wrapTableInResponsiveContainer, getColumnsAndRowsFromCells, isTableNode, getSelectedTableNode, getParentRowFromNode, asPercentage } from '../utils/table';
import { trackInteraction } from '../utils/usageTracking';
import { EDIT_TABLE, INSERT_TABLE } from '../constants/usageTracking';
import { setTableStyles, trackEditTableStyle } from './util';
function setNodeHeight(editor, node, height) {
  editor.dom.setStyle(node, 'height', height);
}
export function getTableResizeHandler(editor) {
  return ({
    target
  }) => {
    if (target.nodeName === 'TABLE') {
      const {
        [hstableConfig.USE_MAX_DIMENSION_WIDTH]: maxTableWidth = Number.MAX_SAFE_INTEGER
      } = getPluginSetting(editor, TABLE_SETTINGS, {});
      const newRawTableWidth = target.style.width;
      const newTableWidth = Math.round(parseFloat(newRawTableWidth, 10));
      if (isResponsiveTableNode(target)) {
        editor.dom.setStyle(target.parentNode, 'height', null);
        if (isPercentage(newRawTableWidth)) {
          const containerWidth = Math.min((target.parentNode.parentNode || target.parentNode).offsetWidth, maxTableWidth);
          const calculatedWidth = Math.round(target.offsetWidth / containerWidth * 100);
          editor.dom.setStyle(target.parentNode, 'width', `${Math.min(calculatedWidth, 100)}%`);
          // Always keep the table element at 100% width
          editor.dom.setStyle(target, 'width', '100%');
        } else {
          if (newTableWidth > maxTableWidth) {
            editor.dom.setStyle(target, 'width', `${maxTableWidth}px`);
          }
          // Always set the wrapper width since the table resizes automatically
          editor.dom.setStyle(target.parentNode, 'width', `${Math.min(newTableWidth, maxTableWidth)}px`);
        }
        return;
      }
      if (newRawTableWidth.includes('px') && newTableWidth > maxTableWidth) {
        editor.dom.setStyle(target, 'width', `${maxTableWidth}px`);
      }
    }
  };
}

/**
 * @TODO Remove this once `useTableContextualToolbar` is fully ungated
 */
export const getTableLayoutChangeHandler = (editor, table) => (layout, oldLayout) => {
  const {
    [hstableConfig.USE_ALIGNMENT_CLASSES]: useAlignmentClasses
  } = getPluginSetting(editor, TABLE_SETTINGS, {});
  if (layout && layout === oldLayout) {
    layout = Alignments.NONE;
  }
  const targetNode = isResponsiveTableNode(table) ? table.parentElement : table;
  editor.undoManager.transact(() => {
    if (useAlignmentClasses) {
      Object.values(TableAlignmentClasses).forEach(alignmentClass => editor.dom.removeClass(targetNode, alignmentClass));
      editor.dom.addClass(targetNode, TableAlignmentClasses[layout]);
    } else {
      const layoutStyles = TableAlignmentStyles[layout];
      if (layoutStyles) {
        editor.dom.setStyle(targetNode, layoutStyles);
      }
    }
  });
  editor.fire('TableLayoutChanged');
};
export const getInsertTableHandler = editor => {
  const {
    [hstableConfig.USE_ALIGNMENT_CLASSES]: useAlignmentClasses
  } = getPluginSetting(editor, TABLE_SETTINGS, {});
  const useTableContextualToolbar = editor.getParam('useTableContextualToolbar');
  return (cols, rows) => {
    editor.undoManager.transact(() => {
      const tableElm = editor.plugins.table.insertTable(cols, rows);
      editor.execCommand('hsInsertNewline', false, {
        target: tableElm
      });
      if (useAlignmentClasses) {
        editor.dom.addClass(tableElm, TableAlignmentClasses[Alignments.CENTER]);
      }
      wrapTableInResponsiveContainer(editor, tableElm);
    });
    trackInteraction(editor, 'tinymceInsertTable', INSERT_TABLE, {
      version: useTableContextualToolbar ? 'contextualToolbar' : 'v1',
      columns: cols,
      rows
    });
  };
};
export const getDeleteTableHandler = editor => {
  const command = 'mceTableDelete';
  const useTableContextualToolbar = editor.getParam('useTableContextualToolbar');
  return table => {
    editor.undoManager.transact(() => {
      if (isResponsiveTableNode(table)) {
        editor.dom.remove(table.parentNode);
      } else {
        if (useTableContextualToolbar) {
          editor.dom.remove(table);
        } else {
          editor.execCommand(command);
        }
      }
    });
    trackInteraction(editor, 'tinymceEditTable', EDIT_TABLE, {
      version: useTableContextualToolbar ? 'contextualToolbar' : 'v1',
      command
    });
  };
};
function flatten(arr) {
  return [].concat(...arr);
}
export const handleTableDimensionChange = (editor, tableNodes, dimensionType, dimensions, {
  tableWidth
}) => {
  const {
    width,
    height
  } = dimensions;
  if (isTableNode(tableNodes[0])) {
    /**
     * Change dimensions for the table node
     */
    const tableNode = tableNodes[0];
    if (dimensionType === 'width') {
      // Change table width
      const units = String(tableWidth).includes('%') ? '%' : 'px';
      const valueWithUnits = `${width}${units}`;
      if (typeof width === 'number') {
        editor.undoManager.transact(() => {
          const isResponsive = isResponsiveTableNode(tableNode);
          if (isResponsive) {
            editor.dom.setStyle(tableNode.parentNode, 'width', valueWithUnits);
          }
          if (!isResponsive || units === 'px') {
            editor.dom.setStyle(tableNode, 'width', valueWithUnits);
          }
        });
        trackEditTableStyle(editor, 'table-width');
      }
    } else {
      // Change table height
      if (typeof height === 'number') {
        setTableStyles(editor, tableNodes, {
          height: `${height}px`
        });
      }
    }
  } else {
    /**
     * Change dimensions for cell(s)
     */
    const tableCells = getColumnsAndRowsFromCells(tableNodes);
    const parentTable = getSelectedTableNode(editor);
    if (dimensionType === 'width') {
      const units = String(tableWidth).includes('%') ? '%' : 'px';
      const styles = {
        width: `${dimensions.width}${units}`
      };
      if (units === '%') {
        setTableStyles(editor, flatten(tableCells.columns), styles);
      } else {
        // Update the table width since cell widths are the defining agents
        editor.dom.setStyle(parentTable, 'width', null);
        if (isResponsiveTableNode(parentTable)) {
          editor.dom.setStyle(parentTable.parentNode, 'width', null);
        }
        setTableStyles(editor, flatten(tableCells.columns), styles);
        handleTableDimensionChange(editor, [parentTable], 'width', {
          width: parentTable.offsetWidth
        }, {
          tableWidth
        });
      }
    } else {
      editor.dom.setStyle(parentTable, 'height', null);
      editor.dom.setAttrib(parentTable, 'height', null);
      if (isResponsiveTableNode(parentTable)) {
        editor.dom.setStyle(parentTable.parentNode, 'height', null);
        editor.dom.setAttrib(parentTable.parentNode, 'height', null);
      }
      const cellNodes = flatten(tableCells.rows);
      const rowNodes = tableCells.rows.map(tds => getParentRowFromNode(tds[0]));
      const styles = {
        height: `${height}px`
      };
      setTableStyles(editor, cellNodes, styles);
      rowNodes.forEach(rowNode => setNodeHeight(editor, rowNode, height));
    }
    trackEditTableStyle(editor, `cell-${dimensionType}`);
  }
};
export const handleTableAlignmentChange = (editor, tableNode, alignment, prevAlignment) => {
  const {
    [hstableConfig.USE_ALIGNMENT_CLASSES]: useAlignmentClasses
  } = getPluginSetting(editor, TABLE_SETTINGS, {});
  if (alignment && alignment === prevAlignment) {
    alignment = Alignments.NONE;
  }
  const targetNode = isResponsiveTableNode(tableNode) ? tableNode.parentNode : tableNode;
  editor.undoManager.transact(() => {
    if (useAlignmentClasses) {
      Object.values(TableAlignmentClasses).forEach(alignmentClass => editor.dom.removeClass(targetNode, alignmentClass));
      editor.dom.addClass(targetNode, TableAlignmentClasses[alignment]);
    } else {
      const layoutStyles = TableAlignmentStyles[alignment];
      if (layoutStyles) {
        editor.dom.setStyle(targetNode, layoutStyles);
      }
    }
  });
  trackEditTableStyle(editor, 'tableLayout');
};
export const handleTableMarginChange = (editor, tableNode, margin) => {
  const {
    top,
    left,
    right,
    bottom
  } = margin;
  const targetNode = isResponsiveTableNode(tableNode) ? tableNode.parentNode : tableNode;
  editor.undoManager.transact(() => {
    editor.dom.setStyles(targetNode, {
      'margin-top': top,
      'margin-left': left,
      'margin-right': right,
      'margin-bottom': bottom
    });
  });
  trackEditTableStyle(editor, 'tableMargin');
};
export const handleTableDimensionTypeChange = (editor, tableNode, dimensionType, tablePixelWidth) => {
  const {
    [hstableConfig.MAX_DIMENSION_WIDTH]: maxTableWidth
  } = getPluginSetting(editor, TABLE_SETTINGS, {});
  editor.undoManager.transact(() => {
    let updatedTableWidth;
    if (dimensionType === TableDimensionTypes.PIXELS) {
      // Convert table and cell widths to pixels
      Array.from(tableNode.querySelectorAll('td')).filter(
      // Ignore nested table cells
      cellNode => editor.dom.getParent(cellNode, 'table') === tableNode).forEach(cell => {
        const style = editor.dom.serializeStyle(Object.assign({}, editor.dom.parseStyle(editor.dom.getAttrib(cell, 'style')), {
          width: `${cell.offsetWidth}px`
        }));
        editor.dom.setAttribs(cell, {
          style
        });
      });
      const convertedWidth = `${tableNode.offsetWidth}px`;
      updatedTableWidth = convertedWidth;
      if (isResponsiveTableNode(tableNode)) {
        editor.dom.setStyle(tableNode.parentNode, 'width', convertedWidth);
      }
    } else {
      // Convert table and cell widths to percentages
      const wrapperNode = isResponsiveTableNode(tableNode) ? tableNode.parentNode : tableNode;
      const containerWidth = maxTableWidth ? Math.min(wrapperNode.parentNode.offsetWidth, maxTableWidth) : wrapperNode.parentNode.offsetWidth;
      Array.from(tableNode.querySelectorAll('td')).filter(
      // Ignore nested table cells
      cellNode => editor.dom.getParent(cellNode, 'table') === tableNode).forEach(cell => {
        const style = editor.dom.serializeStyle(Object.assign({}, editor.dom.parseStyle(editor.dom.getAttrib(cell, 'style')), {
          width: `${asPercentage(cell.offsetWidth, tablePixelWidth)}%`
        }));
        editor.dom.setAttribs(cell, {
          style
        });
      });
      const convertedWidth = `${asPercentage(tableNode.offsetWidth, containerWidth)}%`;
      updatedTableWidth = convertedWidth;
      if (isResponsiveTableNode(tableNode)) {
        editor.dom.setStyle(tableNode.parentNode, 'width', convertedWidth);
        updatedTableWidth = `100%`;
      }
    }
    editor.dom.setStyle(tableNode, 'width', updatedTableWidth);
  });
  trackEditTableStyle(editor, 'tableWidthType');
};
export const getCloneTableHandler = editor => tableNode => {
  editor.undoManager.transact(() => {
    const isResponsive = isResponsiveTableNode(tableNode);
    const tableWrapper = isResponsive ? tableNode.parentNode : tableNode;
    const tableClone = tableWrapper.cloneNode(true);
    editor.dom.insertAfter(tableClone, tableWrapper);
    editor.selection.select(tableClone);
  });
  trackEditTableStyle(editor, 'tableClone');
};